import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { BaseTable } from "src/components/BaseTable/BaseTable";
import Page from "src/components/Page";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { TransactionList } from "../transactions.hook";
import { transactionsListColumns } from "./TransactionsList.constants";
import { Context } from "src/context";
import { useNavigate } from "react-router-dom";
import { useAccess } from "src/hooks/useAccess.hook";
import { FEATURE } from "src/util/enums";
import { useState, useEffect, useMemo, useRef, useCallback, useContext } from "react";
import {
  SpeedDialAction,
  SpeedDialIcon,
  CircularProgress,
  Typography,
} from "@mui/material";
import { downloadExcel } from "src/utils/downloadExcel";
import {
  getAllExistedTeam,
  transactionExcelDownload,
} from "src/react-query/endPoints";
import axios from "../../../util/axios";
import TransactionPayoutUpdModal from "./TransactionPayoutUpdModal";
import { StyledSpeedDial } from "src/common/components/StyledSpeedDial";
import { Icon } from "@iconify/react";
import editFill from "@iconify/icons-eva/edit-fill";
import { useIsGPUser } from "src/hooks/useIsGPUser.hook";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { useAuth } from "src/hooks/useAuth.hook";
import { CommonSearchbar } from "src/layouts/dashboard/Searchbar";
import { useListControls } from "src/hooks/useListControls.hook";
import InvoiceTemplate from "src/components/cmCustomer/InvoiceTemplate";
import { generateTransactionPDFFromHTML } from "src/components/cmCustomer/TransactionPdf";
import { TabContext, TabPanel } from "@mui/lab";
import { RootTabItemStyles, RootTabStyle } from "src/theme/overrides/Tabs";
import InvoiceExportDownloadTab from "../InvoiceExport/InvoiceExportDownloadTab";

const TRANSACTION_TAB = {
  LIST: "Transactions",
  DOWNLOADS: "Download",
};

const getTransactionTabs = (teamType) => [
  { value: TRANSACTION_TAB.LIST, label: "Transactions" },
  ...(teamType === "T"
    ? [{ value: TRANSACTION_TAB.DOWNLOADS, label: "Download" }]
    : []),
];

export const TransactionsList = () => {
  const navigate = useNavigate();
  const { teamId, teamType } = useSelectedTeam();
  const { timezone } = useContext(Context);

  const isReadOnly = useAccess(FEATURE.TRANSACTION, "R");

  //CHECKS FOR THE PAYOUTS DETAILS MODAL
  const isUserFromGP = useIsGPUser();
  const hasTransactionReadAccess = useAccess(FEATURE.TRANSACTION, "V");
  // const hasPayoutReadAccess = useAccess(FEATURE.FINANCE, "V");

  const [activeTab, setActiveTab] = useState(TRANSACTION_TAB.LIST);
  const [openPayoutsDetails, setOpenPayoutsDetails] = useState(false);
  const invoiceRef = useRef();
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [currentDownloadingId, setCurrentDownloadingId] = useState(null);

  // const { data, isFetching } = useTransactions({ teamId });
  const [tableData, setTableData] = useState([]);
  const { user } = useAuth();
  const [selectedTeam, setSelectedTeam] = useState([]);
  const [allTeamsData, setAllTeamsData] = useState();
  const [dummyTeamData, setDummyTeamData] = useState();
  const [selectedGPTeam, setSelectedGPTeam] = useState([]);
  const {
    pagination,
    handlePageChange,
    handlePageSizeChange,
    updateTotal,
    search,
    handleSearch,
    resetPage,
  } = useListControls(10);
  const companyType = useMemo(() => {
    return user?.company?.companyType;
  }, [user]);

  const transactionTabs = useMemo(
    () => getTransactionTabs(teamType),
    [teamType]
  );

  const { data, isFetching } = TransactionList({
    teamId,
    page: pagination.page,
    pageSize: pagination.pageSize,
    filterteam: selectedTeam,
    filterGPTeam: selectedGPTeam,
    companyType,
    search,
  });

  const downloadInvoice = useCallback(async (row) => {
    if (isGeneratingPDF) {
      return false;
    }

    const transactionId = row?.transactionId || row?.id;
    if (!transactionId) {
      console.error("Missing transactionId while downloading invoice", row);
      return false;
    }

    setIsGeneratingPDF(true);
    setCurrentDownloadingId(transactionId);

    try {
      const fullUrl = `${process.env.REACT_APP_CSMS_NEST_ENDPOINT}/invoice/send/evdriver`;
      const invoiceData = await axios.post(
        fullUrl,
        {
          transactionId,
          ...(row?.email && { email: row.email }),
          getPdf: true,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const content =
        invoiceData?.data?.data?.template_content || invoiceData?.data?.template_content;

      if (!content) {
        console.error("No invoice template content returned from API", invoiceData?.data);
        return false;
      }

      setSelectedTransaction(content);
      await generateTransactionPDFFromHTML(invoiceRef, transactionId, content);
    } catch (error) {
      console.error("Error downloading transaction invoice:", error);
    } finally {
      setIsGeneratingPDF(false);
      setCurrentDownloadingId(null);
    }

    return false;
  }, [isGeneratingPDF]);

  const initialColumns = useMemo(
    () =>
      transactionsListColumns(
        isReadOnly,
        downloadInvoice,
        isGeneratingPDF,
        currentDownloadingId,
        timezone.timezone?.value
      ),
    [isReadOnly, downloadInvoice, isGeneratingPDF, currentDownloadingId, timezone.timezone?.value]
  );

  const finalTableColumns = useMemo(
    () => initialColumns.filter(({ field }) => field !== "status" && field !== "notes"),
    [initialColumns]
  );
  const rowOrCellClickRedirect = ({ id }) => {
    // if(isReadOnly) return;
    navigate(`/transactions/${id}`);
  };

  useEffect(() => {
    if (user?.company?.id === teamId) {
      setAllTeamsData(dummyTeamData);
    } else {
      const team = dummyTeamData?.filter(
        (list) => list.parentCompanyId === teamId
      );
      setAllTeamsData(team);
    }
  }, [teamId, dummyTeamData]);

  useEffect(() => {
    try {
      const getAllTeam = async () => {
        const response = await axios.get(getAllExistedTeam());
        setAllTeamsData(response.data.data);
        setDummyTeamData(response.data.data);
      };
      companyType === "GP" && getAllTeam();
    } catch (err) {
      console.log(err);
    }
  }, [companyType]);

  useEffect(() => {
    setTableData(data?.transactions);
  }, [data]);

  useEffect(() => {
    updateTotal(data?.totalRecord || 0);
  }, [data?.totalRecord, updateTotal]);

  const saveToExcel = async () => {
    try {
      const response = await axios.get(transactionExcelDownload({
        teamId,
        filterteam: selectedTeam,
        filterGPTeam: selectedGPTeam,
        companyType,
        tz: timezone.timezone?.value,
      }), {
        responseType: "blob",
      });

      downloadExcel(response.data, "transaction");
    } catch (error) {
      console.error("Error downloading the Excel file", error);
    }
  };

  const openClosePayoutsDetails = () => setOpenPayoutsDetails((prev) => !prev);
  const speedDialActions = [
    {
      icon: (
        <Icon
          icon="heroicons:document-currency-rupee-solid"
          width="27"
          height="27"
        />
      ),
      name: "Payouts",
      onClick: openClosePayoutsDetails,
    },
  ];
  const onChangeCompany = (event) => {
    setSelectedTeam(event.target.value);
    setSelectedGPTeam([]);
    resetPage();
    if (companyType === "GP" && event.target.value.length > 0) {
      const teamFilterValue = dummyTeamData.filter((filter) =>
        event.target.value.includes(filter.parentCompanyId)
      );
      setAllTeamsData(teamFilterValue);
    } else setAllTeamsData(dummyTeamData);
  };

  return (
    <Page title="Transactions | Smart-CMS">
      <TabContext value={activeTab}>
        {transactionTabs.length > 1 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <RootTabStyle
              indicatorColor="transparent"
              centered
              length={transactionTabs.length}
              onChange={(_, value) => setActiveTab(value)}
            >
              {transactionTabs.map((tab) => (
                <RootTabItemStyles
                  key={tab.value}
                  value={tab.value}
                  label={tab.label}
                />
              ))}
            </RootTabStyle>
          </Box>
        )}
        <TabPanel value={TRANSACTION_TAB.LIST} sx={{ px: 0 }}>
      <Card sx={{ py: 2, px: 1, width: "100%" }}>

        {/* <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 2, px: 2 }}>
          <Grid item xs={12} sm={8}> */}
            <Box
              sx={{
                display: "flex",
                // flexWrap: "wrap",
                // gridTemplateColumns: {xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)",},
                gridColumnGap: "0.8rem",
                alignItems: "center",
                padding: "15px",
                marginRight: { xs: "-45px", sm: "0" },
                marginLeft: { xs: "-13px", sm: "0" },
              }}
            >
            
              {(companyType === "GP" || companyType === "P") &&
                user?.company?.id === teamId && (
                  <Box sx={{ width: { xs: "120%", sm: "25%" } }}>
                    <FormControl fullWidth size="small"
                      sx={{
                        fontSize: { xs: "0.7rem", sm: "1rem" }, // Adjust font size for responsiveness
                      }}>
                      <InputLabel id="demo-simple-select-label"
                        sx={{
                          fontSize: { xs: "0.75rem", sm: "1rem" }, // Responsive label font size
                        }}>
                        {companyType === "GP" ? "Select Company" : "Select Team"}
                      </InputLabel>
                      <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        multiple
                        // label="All Time"
                        value={selectedTeam}
                        onChange={(event) => onChangeCompany(event)}
                        sx={{
                          fontSize: { xs: "0.8rem", sm: "1rem" }, // Adjust select font size
                          height: { xs: 35, sm: 42 }, // Adjust box height
                        }}
                      >
                        {user?.role
                          ?.filter((team) => team.teamId !== user?.company?.id)
                          ?.map((team) => (
                            <MenuItem
                              key={team.teamId}
                              value={team.teamId}
                              sx={{
                                fontSize: { xs: "0.8rem", sm: "1rem" }, // Responsive menu item font size
                                // pl: 4,
                              }}
                            >
                              {team.teamName}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Box>
                )}
              {companyType === "GP" && (
                <Box sx={{ width: { xs: "110%", sm: "25%" } }}>
                  <FormControl fullWidth size="small"
                    sx={{
                      fontSize: { xs: "0.8rem", sm: "1rem" }, // Adjust font size for responsiveness
                    }}>
                    <InputLabel id="demo-simple-select-label"
                      sx={{
                        fontSize: { xs: "0.75rem", sm: "1rem" }, // Responsive label font size
                      }}>
                      Select Team
                    </InputLabel>
                    <Select
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      multiple
                      // label="All Time"
                      value={selectedGPTeam}
                      onChange={(event) => {
                        setSelectedGPTeam(event.target.value);
                        resetPage();
                      }}
                      sx={{
                        fontSize: { xs: "0.8rem", sm: "1rem" }, // Adjust select font size
                        height: { xs: 35, sm: 42 }, // Adjust box height
                      }}
                    >
                      {allTeamsData?.map((team) => (
                        <MenuItem
                          key={team.companyId}
                          value={team.companyId}
                          sx={{
                            fontSize: { xs: "0.8rem", sm: "1rem" }, // Responsive menu item font size
                          }}
                        >
                          {team.companyName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
              {tableData?.length > 0 && (
                <Button
                  variant="contained"
                  onClick={saveToExcel}
                  sx={{
                    whiteSpace: "nowrap",
                    minWidth: { xs: "40px", sm: "80px" }, // Adjust width for small and large screens
                    height: { xs: "35px", sm: "42px" },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    mr: 4,
                  }}
                >
                  <Box sx={{ display: { xs: "flex", sm: "none" } }}>
                    <Icon icon="material-symbols:download" width="20" height="20" />
                  </Box>
                  <Box sx={{ display: { xs: "none", sm: "flex" } }}>Export</Box>
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: "100%", // Position above the button
                      left: "50%",
                      transform: "translateX(-50%)",
                      whiteSpace: "nowrap",
                      background: "black",
                      color: "white",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      fontSize: "12px",
                      visibility: "hidden",
                      opacity: 0,
                      transition: "opacity 0.2s",
                      pointerEvents: "none",
                      zIndex: 1,
                    }}
                    className="tooltip"
                  >
                    Export to Excel
                  </Box>

                </Button>
              )}
              <CommonSearchbar
                placeholder="Search"
                searchFunc={handleSearch}
              />
            </Box>
          {/* </Grid>

          <Grid item xs={12} sm={4} justifyContent="flex-end" display="flex">
            <CommonSearchbar
              placeholder="Search"
              searchFunc={handleSearching}
            />
          </Grid>
        </Grid> */}

        <Box sx={{ position: "relative" }}>
          {isGeneratingPDF && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                zIndex: 10,
                borderRadius: 1,
                backgroundColor: "rgba(255, 255, 255, 0.72)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 2,
                  py: 1.5,
                  borderRadius: 1,
                  backgroundColor: "background.paper",
                  boxShadow: 3,
                }}
              >
                <CircularProgress size={22} />
                <Typography variant="body2" color="text.secondary">
                  {currentDownloadingId
                    ? `Generating invoice for transaction ${currentDownloadingId}...`
                    : "Generating invoice..."}
                </Typography>
              </Box>
            </Box>
          )}
          <BaseTable
            isReadOnly={isReadOnly}
            rows={tableData ?? []}
            columns={finalTableColumns}
            loading={isFetching}
            getRowId={(row) => row.transactionId}
            pageSize={pagination.pageSize}
            showExportCsvBtn={false}
            csvMorphemes="transactions"
            csvColumns={initialColumns}
            rowOrCellClickRedirect={rowOrCellClickRedirect}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            totalRows={pagination.total}
            serverRowFetcher={true}
            rowsPerPageOptions={[10, 30, 50, 100]}
            paginationModel={{ page: pagination.page, pageSize: pagination.pageSize }}
            sx={{
              ".MuiDataGrid-columnHeaderTitle": {
                overflow: "hidden   !important",
                lineHeight: "20px   !important",
                whiteSpace: "normal  !important",
              },
            }}
          />
        </Box>
        <StyledSpeedDial
          ariaLabel="SpeedDial playground example"
          hidden={!isUserFromGP || !hasTransactionReadAccess || isReadOnly}
          icon={
            <SpeedDialIcon
              openIcon={<Icon icon={editFill} width={24} height={24} />}
            />
          }
          direction={"up"}
        >
          {speedDialActions.map((action) => (
            <SpeedDialAction
              key={action.name}
              icon={action.icon}
              tooltipTitle={action.name}
              onClick={action.onClick}
            />
          ))}
        </StyledSpeedDial>
      </Card>
        </TabPanel>
        {teamType === "T" && (
          <TabPanel value={TRANSACTION_TAB.DOWNLOADS} sx={{ px: 0 }}>
            <InvoiceExportDownloadTab isReadOnly={isReadOnly} />
          </TabPanel>
        )}
      </TabContext>
      <TransactionPayoutUpdModal
        open={openPayoutsDetails}
        onClose={openClosePayoutsDetails}
      />
      <div style={{ position: 'fixed', left: '-10000px', top: 0, width: '820px', backgroundColor: '#ffffff' }}>
        <InvoiceTemplate
          ref={invoiceRef}
          transactionData={selectedTransaction}
        />
      </div>
    </Page>
  );
};
